import { useIsSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { getSpacing } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { useResponsiveValue } from "@faire/web/ui/hooks/useResponsiveValue";
import isChromatic from "chromatic/isChromatic";
import React, { useMemo, type JSX } from "react";
import styled from "styled-components";

import { useLoggedOutCategoryPageBannersResponse } from "@faire/retailer/components/Banners/DataFetching/useLoggedOutCategoryPageBannersResponse";
import { LoggedOutCategoryBanners } from "@faire/retailer/components/Banners/LoggedOutCategoryBanners/LoggedOutCategoryBanners";
import { EfficientListLoader } from "@faire/retailer/components/EfficientListLoader/EfficientListLoader";
import { withFallback } from "@faire/retailer/components/GhostLoading/helpers";
import { withBanner } from "@faire/retailer/lib/bannerUtils";

interface ItemListProps<T> {
  items: T[];
  renderItem: (item?: T) => JSX.Element;
  onBatchVisible?: (items: (T | undefined)[]) => void;
  batchFallbackSize?: number;
  hashKey: string | undefined;
}

export const LoggedOutCategoryPageItemList = <T,>(props: ItemListProps<T>) => {
  const isSSR = useIsSSR();
  const { items, renderItem, onBatchVisible, batchFallbackSize = 20 } = props;

  const itemsPerRow = useResponsiveValue({
    mobileAndAbove: 2,
    tabletAndAbove: 4,
  });

  const renderProductTileBatchPlaceholder = (
    placeholderItems?: (T | undefined)[]
  ) => {
    return (
      <React.Fragment>
        {placeholderItems?.map((_, index) => (
          <SingleProductPlaceholder key={`placeholder-${index}`} />
        )) ?? null}
      </React.Fragment>
    );
  };

  const { displayableBannersWithAdjustedRow } =
    useLoggedOutCategoryPageBannersResponse();
  const displayableBanners = useMemo(() => {
    return displayableBannersWithAdjustedRow(itemsPerRow, items.length);
  }, [displayableBannersWithAdjustedRow, itemsPerRow, items.length]);

  const data = useMemo(() => {
    if (items.length) {
      if (displayableBanners.length) {
        const itemsToReturn = withBanner(
          items,
          itemsPerRow,
          displayableBanners
        );
        return itemsToReturn;
      }
      return items;
    }
    return withFallback(items, batchFallbackSize);
  }, [displayableBanners, items, itemsPerRow, batchFallbackSize]);

  return (
    <Wrapper $columns={itemsPerRow}>
      <EfficientListLoader
        hashKey={props.hashKey}
        data={data}
        batchSize={itemsPerRow * 2}
        renderComponent={renderItem}
        // Remember to update the placeholder if the tile changes
        placeholderFn={renderProductTileBatchPlaceholder}
        forceRender={isSSR || isChromatic()}
        threshold={-400}
        filterFakeTile
        onBatchVisible={onBatchVisible}
        visibilityWrapperDisplayProperty="unset"
      />
      <LoggedOutCategoryBanners banners={displayableBanners} />
    </Wrapper>
  );
};

const Wrapper = styled.div<{
  $columns: number;
}>`
  display: grid;
  align-self: stretch;
  gap: ${getSpacing("3x", "1x")};
  grid-template-columns:
    [start] repeat(${(props) => props.$columns}, minmax(0, 1fr))
    [end];
`;

const SingleProductPlaceholder = styled.div`
  width: 100%;
  ${responsiveMediaQueries.mobile} {
    height: 334px;
  }
  ${responsiveMediaQueries.tablet} {
    height: 338px;
  }
  ${responsiveMediaQueries.desktop} {
    height: 360px;
  }
  ${responsiveMediaQueries.xLargeAndAbove} {
    height: 454px;
  }
`;
